import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { OrgEmployee, ProductCategory, MOCK_ORG_CHART, MOCK_PRODUCT_CATEGORIES } from '../mock-data/mock-tree-data';

/**
 * Tree Grid Example
 * Demonstrates hierarchical data rendering with expand/collapse functionality
 */
@Component({
  selector: 'app-tree-grid-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Tree Grid (Datos Jerárquicos)</h2>
        <p class="text-gray-600 mb-2">
          El Tree Grid permite visualizar datos jerárquicos con estructura de árbol, como organigramas, categorías de productos, o estructuras de archivos.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            🌳 Tree Structure
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            🔽 Expand/Collapse
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            📏 Visual Indentation
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            ⚙️ Auto-Expand Depth
          </span>
        </div>

        <!-- Control Buttons -->
        <div class="flex gap-2 mb-4 flex-wrap">
          <button mat-raised-button color="primary" (click)="expandAll()">
            <mat-icon>unfold_more</mat-icon>
            Expandir Todo
          </button>
          <button mat-raised-button color="accent" (click)="collapseAll()">
            <mat-icon>unfold_less</mat-icon>
            Colapsar Todo
          </button>
          <button mat-raised-button [color]="showingOrgChart ? 'primary' : 'basic'" (click)="switchToOrgChart()">
            <mat-icon>people</mat-icon>
            Organigrama
          </button>
          <button mat-raised-button [color]="!showingOrgChart ? 'primary' : 'basic'" (click)="switchToCategories()">
            <mat-icon>category</mat-icon>
            Categorías
          </button>
        </div>

        <!-- Tree Statistics -->
        <div class="p-4 bg-blue-50 dark:bg-blue-900 rounded mb-4" *ngIf="treeStats">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            <mat-icon class="text-blue-600">analytics</mat-icon>
            Estadísticas del Árbol:
          </h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <strong>Total de Nodos:</strong> {{ treeStats.totalNodes }}
            </div>
            <div>
              <strong>Nodos Visibles:</strong> {{ treeStats.visibleNodes }}
            </div>
            <div>
              <strong>Nodos Expandidos:</strong> {{ treeStats.expandedNodes }}
            </div>
            <div>
              <strong>Profundidad Máxima:</strong> {{ treeStats.maxDepth + 1 }} niveles
            </div>
          </div>
        </div>
      </div>

      <!-- Organization Chart Grid -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow" *ngIf="showingOrgChart">
        <app-smart-grid
          #orgGrid
          [data]="orgChartData"
          [columnDefs]="orgColumnDefs"
          [config]="orgGridConfig">
        </app-smart-grid>
      </div>

      <!-- Product Categories Grid -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow" *ngIf="!showingOrgChart">
        <app-smart-grid
          #categoriesGrid
          [data]="categoriesData"
          [columnDefs]="categoriesColumnDefs"
          [config]="categoriesGridConfig">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">✨ Características del Tree Grid:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>Estructura Jerárquica:</strong> Visualiza parent-child relationships con indentación</li>
          <li><strong>Expand/Collapse Individual:</strong> Click en el icono para expandir/colapsar nodos</li>
          <li><strong>Expand/Collapse Global:</strong> Controles para expandir/colapsar todo el árbol</li>
          <li><strong>Auto-Expand Depth:</strong> Configurable para expandir automáticamente N niveles</li>
          <li><strong>Indentación Visual:</strong> Tamaño de indentación configurable por nivel</li>
          <li><strong>Iconos Dinámicos:</strong> Chevron para collapsed, ExpandMore para expanded</li>
          <li><strong>Performance Optimizada:</strong> Solo renderiza nodos visibles (collapsed children hidden)</li>
          <li><strong>Type-Safe:</strong> Full TypeScript support con children field configurable</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">⚙️ Configuración del Tree Grid:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{configExample}}</code></pre>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Estructura de Datos:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{dataExample}}</code></pre>
      </div>
    </div>
  `,
})
export class TreeGridExampleComponent {
  @ViewChild('orgGrid') orgGrid!: SmartGridComponent<OrgEmployee>;
  @ViewChild('categoriesGrid') categoriesGrid!: SmartGridComponent<ProductCategory>;

  // Current view
  showingOrgChart: boolean = true;

  // Tree statistics
  treeStats: any = null;

  // ============================================================================
  // ORGANIZATION CHART DATA
  // ============================================================================

  orgChartData: OrgEmployee[] = MOCK_ORG_CHART;

  orgColumnDefs: ColDef<OrgEmployee>[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      width: 250,
    },
    {
      field: 'position',
      headerName: 'Puesto',
      width: 250,
    },
    {
      field: 'department',
      headerName: 'Departamento',
      width: 200,
      filterable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
    },
    {
      field: 'salary',
      headerName: 'Salario',
      width: 150,
      valueFormatter: (value) => `${value.toLocaleString()}`,
      cellClass: 'text-right',
    },
    {
      field: 'hireDate',
      headerName: 'Fecha de Contratación',
      width: 180,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  orgGridConfig: GridConfig<OrgEmployee> = {
    treeData: {
      enabled: true,
      childrenField: 'children',
      expandDepth: 1, // Expand only first level by default
      showExpandControls: true,
      indentSize: 20,
    },
    pagination: {
      enabled: false,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
    },
    search: {
      enabled: true,
      showSearchInput: true,
      placeholder: 'Buscar empleado, puesto, departamento...',
      searchableFields: ['name', 'position', 'department', 'email'],
    },
    columnFilters: {
      enabled: true,
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
    },
    styling: {
      maxHeight: '600px',
    },
  };

  // ============================================================================
  // PRODUCT CATEGORIES DATA
  // ============================================================================

  categoriesData: ProductCategory[] = MOCK_PRODUCT_CATEGORIES;

  categoriesColumnDefs: ColDef<ProductCategory>[] = [
    {
      field: 'name',
      headerName: 'Categoría',
      width: 300,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 400,
    },
    {
      field: 'productCount',
      headerName: 'Cantidad de Productos',
      width: 180,
      cellClass: 'text-right',
    },
    {
      field: 'active',
      headerName: 'Activa',
      width: 100,
      valueFormatter: (value) => value ? 'Sí' : 'No',
      cellClass: (params) => params.data?.active ? 'text-green-600' : 'text-red-600',
      filterable: true,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de Creación',
      width: 180,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  categoriesGridConfig: GridConfig<ProductCategory> = {
    treeData: {
      enabled: true,
      childrenField: 'children',
      expandDepth: 2, // Expand two levels by default
      showExpandControls: true,
      indentSize: 24,
    },
    pagination: {
      enabled: false,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
    },
    search: {
      enabled: true,
      showSearchInput: true,
      placeholder: 'Buscar categoría...',
      searchableFields: ['name', 'description'],
    },
    columnFilters: {
      enabled: true,
    },
    styling: {
      maxHeight: '600px',
    },
  };

  // ============================================================================
  // METHODS
  // ============================================================================

  ngAfterViewInit(): void {
    // Update stats after view init
    setTimeout(() => this.updateStats(), 100);
  }

  expandAll(): void {
    const currentGrid = this.showingOrgChart ? this.orgGrid : this.categoriesGrid;
    if (currentGrid) {
      currentGrid.expandAllTreeNodes();
      this.updateStats();
    }
  }

  collapseAll(): void {
    const currentGrid = this.showingOrgChart ? this.orgGrid : this.categoriesGrid;
    if (currentGrid) {
      currentGrid.collapseAllTreeNodes();
      this.updateStats();
    }
  }

  switchToOrgChart(): void {
    this.showingOrgChart = true;
    setTimeout(() => this.updateStats(), 100);
  }

  switchToCategories(): void {
    this.showingOrgChart = false;
    setTimeout(() => this.updateStats(), 100);
  }

  private updateStats(): void {
    const currentGrid = this.showingOrgChart ? this.orgGrid : this.categoriesGrid;
    if (currentGrid) {
      this.treeStats = currentGrid.getTreeStats();
    }
  }

  // ============================================================================
  // EXAMPLE CODE
  // ============================================================================

  configExample = `// Grid Configuration
gridConfig: GridConfig<Employee> = {
  treeData: {
    enabled: true,              // Enable tree mode
    childrenField: 'children',  // Field containing children array
    expandDepth: 1,             // Auto-expand 1 level (0 = none, -1 = all)
    showExpandControls: true,   // Show expand/collapse icons
    indentSize: 20,             // Indentation per level (pixels)
  },
  pagination: {
    enabled: false,             // Disable pagination for tree view
  },
  sorting: {
    enabled: true,              // Sorting maintains tree structure
  },
  search: {
    enabled: true,              // Search across tree
    searchableFields: ['name', 'position', 'department'],
  },
};

// Programmatic Control
grid.expandAllTreeNodes();      // Expand all nodes
grid.collapseAllTreeNodes();    // Collapse all nodes
grid.expandTreeNode(node, true);  // Expand node + children
grid.getTreeStats();            // Get tree statistics`;

  dataExample = `// Data Structure
interface Employee {
  _id: string;
  name: string;
  position: string;
  department: string;
  email: string;
  salary: number;
  hireDate: Date;
  children?: Employee[];  // ⚠️ Children field for hierarchy
}

const orgData: Employee[] = [
  {
    _id: '1',
    name: 'CEO',
    position: 'Chief Executive Officer',
    // ... other fields
    children: [
      {
        _id: '2',
        name: 'CTO',
        position: 'Chief Technology Officer',
        children: [
          { _id: '3', name: 'Lead Developer', /* ... */ },
          { _id: '4', name: 'DevOps Engineer', /* ... */ }
        ]
      },
      {
        _id: '5',
        name: 'CFO',
        position: 'Chief Financial Officer',
        children: [
          { _id: '6', name: 'Accountant', /* ... */ }
        ]
      }
    ]
  }
];`;
}
